import { Patient, SearchPatientsFilters } from "src/common/models/patient"
import { PageCard } from "src/common/styles/styles"
import { InputSearchForm, PatientSearchType } from "./InputSearchForm"
import { useDispatch, useSelector } from "react-redux"
import { searchPatientsApi } from "src/features/MedicalArea/state/action"
import { RootState } from "src/state/reducer"
import { serviceLoading } from "src/common/apiLoader/state/selection"
import { GET_PATIENT_BY_ID_API, SEARCH_PATIENTS_API } from "src/features/MedicalArea/state/actionTypes"
import InnerLoader from "src/components/innerLoader"
import { List, Tooltip } from "antd"
import VirtualList from 'rc-virtual-list';
import EmptyPatientsState from "../EmptyPatientsState"
import { SearchPatientsResponse } from "src/features/MedicalArea/state/types"
import React, { useEffect, useState } from "react"
import styled from "styled-components"
import { SearchOutlined } from "@ant-design/icons";
import { SystemConstants } from "src/common/constants"
import { MailOutlined, PhoneOutlined, IdcardOutlined, UserOutlined } from '@ant-design/icons';
import * as Styles from "./styles"
import { SystemDescriptions } from "src/common/descriptions/descriptions"

interface Props {
    onPatientChange: (patient?: Patient) => void
}

interface ReduxProps {
    isSearchingPatients: boolean
    isGettingPatientById: boolean
    patientsSearchResults?: SearchPatientsResponse
}

const InfoBar = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.85rem;
  color: #6b7280;
  padding: 4px 8px;
  margin-top: 8px;
  border-radius: 6px;
  background: #fafafa;
  border: 1px solid #f0f0f0;

  .anticon {
    font-size: 14px;
    color: #9ca3af;
  }

  strong {
    color: #2563eb;
    font-weight: 500;
  }
`;

interface SearchData {
    searchType: string;
    searchValue: string;
}

export const InputSearch = (props: Props) => {
    const dispatch = useDispatch()

    const [searchData, setSearchData] = useState<SearchData | undefined>(undefined)
    const [isPatientSelected, setIsPatientSelected] = useState<boolean>(false)

    const descriptions = SystemDescriptions.COMPONENTS.PATIENT_SEARCH

    const patientTypeDictionary: { [key in PatientSearchType]: string } = {
        [PatientSearchType.NAME]: descriptions.SEARCH_FORM.SEARCH_TYPE.NAME,
        [PatientSearchType.SURNAME]: descriptions.SEARCH_FORM.SEARCH_TYPE.SURNAME,
        [PatientSearchType.IDENTIFICATION]: descriptions.SEARCH_FORM.SEARCH_TYPE.IDENTIFICATION,
        [PatientSearchType.PHONE]: descriptions.SEARCH_FORM.SEARCH_TYPE.PHONE,
        [PatientSearchType.ID]: descriptions.SEARCH_FORM.SEARCH_TYPE.ID,
        [PatientSearchType.EMAIL]: descriptions.SEARCH_FORM.SEARCH_TYPE.EMAIL,
    }

    const reduxProps: ReduxProps = useSelector((state: RootState) => ({
        isSearchingPatients: serviceLoading(state, [SEARCH_PATIENTS_API]),
        isGettingPatientById: serviceLoading(state, [GET_PATIENT_BY_ID_API]),
        patientsSearchResults: state.medicalArea.patientsSearchResults,
    }))

    const onFinish = (filters: SearchPatientsFilters) => {
        let searchType = "";
        let searchValue = "";

        for (const [key, value] of Object.entries(filters)) {
            if (value !== undefined && searchType === "") {
                searchType = key;
                searchValue = String(value);
                break;
            }
        }

        setSearchData({ searchType, searchValue })

        dispatch(searchPatientsApi(filters))
    }

    const renderDetailItem = (label: string, icon: React.ReactNode, tooltip: string = "") => {
        return <Tooltip placement="topLeft" title={tooltip}>
            <span>{icon} {label}</span>
        </Tooltip>
    }

    const renderPatientData = (patient: Patient) => {
        return <div style={{ display: "flex", gap: "8px" }} >
            {renderDetailItem(patient.email, <MailOutlined />, patientTypeDictionary["email"])} |
            {renderDetailItem(patient.phone, <PhoneOutlined />, patientTypeDictionary["phone"])} |
            {renderDetailItem(patient.doc_id, <IdcardOutlined />, patientTypeDictionary["identification"])} |
            {renderDetailItem(patient.id_patient?.toString() ?? "", <UserOutlined />, patientTypeDictionary["id"])}
        </div>
    }

    const handleOnClickListItem = (patient: Patient) => {
        setIsPatientSelected(true)
        props.onPatientChange(patient)
    }

    useEffect(() => {
        setIsPatientSelected(false)
    }, [reduxProps.patientsSearchResults])

    const renderSearchResults = () => {
        if (reduxProps.isSearchingPatients || reduxProps.isGettingPatientById) {
            return <InnerLoader />
        }

        if (reduxProps.patientsSearchResults?.patients?.length === 0) {
            return <EmptyPatientsState />
        }

        return reduxProps.patientsSearchResults?.patients && reduxProps.patientsSearchResults?.patients.length > 0
            ? <List>
                <InfoBar>
                    <SearchOutlined />

                    {
                        reduxProps.patientsSearchResults?.total > 0
                        && <span>
                            {
                                descriptions.SEARCH_RESULTS.INFO_BAR.INFO_TITLE(patientTypeDictionary[searchData?.searchType || ""], searchData?.searchValue || "")
                            }
                            &nbsp;
                            {
                                reduxProps.patientsSearchResults?.total! > SystemConstants.SEARCH_PATIENTS_LIMIT
                                    ? descriptions.SEARCH_RESULTS.INFO_BAR.TOTAL_COMPLETE_RESULTS(reduxProps.patientsSearchResults?.patients.length, reduxProps.patientsSearchResults?.total!)
                                    : descriptions.SEARCH_RESULTS.INFO_BAR.TOTAL_PARTIAL_RESULTS(reduxProps.patientsSearchResults?.patients.length)
                            }

                        </span>
                    }
                </InfoBar>
                <VirtualList
                    data={reduxProps.patientsSearchResults?.patients || []}
                    itemKey="id_patient"
                >
                    {(item: Patient) => (
                        <Styles.ListItem
                            key={item.id_patient}
                            onClick={() => handleOnClickListItem(item)}
                        >
                            <List.Item.Meta
                                // avatar={<Avatar src={item.avatar} />}
                                title={<span>{`${item.name} ${item.surname}`}</span>}
                                description={renderPatientData(item)}
                            />
                            <div>
                            </div>
                        </Styles.ListItem>
                    )}
                </VirtualList>
            </List>
            : null
    }

    return (
        <PageCard
            size="small"
            title={descriptions.TITLE}
        >
            <InputSearchForm
                onFinish={onFinish}
                loading={reduxProps.isSearchingPatients || reduxProps.isGettingPatientById}
            />
            {!isPatientSelected && renderSearchResults()}
        </PageCard>
    )
}
